import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import type {Block, Editor} from 'grapesjs';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {Dropdown} from '@Components/dropdown-v2';
import {Icon} from '@Components/icon-v2';
import {Image} from 'react-bootstrap';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {TextBlockPanel} from '@Components/email-maker-block-manager/components/text-block-panel/text-block-panel';
import {ImageBlockPanel} from '@Components/email-maker-block-manager/components/image-block-panel';
import {ElementsBlockPanel} from '@Components/email-maker-block-manager/components/elements-block-panel';
import {EmailMakerBlock} from '@Components/email-maker-block-manager/components/email-maker-block';
import {getUniqueString} from '@Utils/string.util';
import {SectionsBlockPanel} from '@Components/email-maker-block-manager/components/sections-block-panel';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import {
  getAllEmailComponentTemplateCategories,
  getAllEmailComponentTemplates,
  getUserEmailComponentTemplates,
  hasUserSeenElementsBlock,
  hasUserSeenSectionsBlock,
  markUserHasSeenElementsBlock,
  markUserHasSeenSectionsBlock,
} from '@Libraries/email-component-template-library';
import type {BlockCategoryMap} from '@Components/email-maker-block-manager/email-maker-block-manager.types';
import {setCategories, setSelectedBlockPanel, setTemplates, setUserTemplates} from '@Components/email-maker-block-manager/email-maker-block-manager.reducer';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import {getAssetUrl} from '@Utils/s3.util';
import {GrapesjsEditorContext, IsMobileScreenContext} from './email-maker-block-manager-grapesjs-editor-context';
import {useAppDispatch} from '@/hooks';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {MobilePanelHeader} from '@Components/email-maker-block-manager/components/mobile-panel-header';
import {useSelectedBlockPanel} from '@Components/email-maker-block-manager/email-maker-block-manager.hooks';
import {GA4EventName} from '@Libraries/ga-events';
import {BannerStyle} from '@/email-maker/grapesjs-email-editor-plugin/src/commands/editor';
import styles from './email-maker-block-manager.module.scss';
import {isUserPremium, isUserLoggedIn} from '@Libraries/user.library';
import {openAuthenticateModal} from '@Modals/authenticate-modal';
import {isModalOpen} from '@Components/modal-container';

export interface EmailMakerBlockManagerProps {
  gjsEditor: Editor;
}

export const POPUP_PANEL_ID = 'email-maker-popup-panel-id';

const AI_EDIT_SIGNUP_SOURCE = 'email-maker-ai-edit';

const PANEL_OFFSET_LEFT = 6;
const PANEL_ARROW_OFFSET = -6;

const INTRO_POPUP_OFFSET_LEFT = 41;
const INTRO_POPUP_ARROW_OFFSET = 31;
const INTRO_POPUP_SHOW_DELAY_MS = 0;
const INTRO_POPUP_HIDE_DELAY_MS = 100;

const POPUP_TOP_BOTTOM_MARGIN = 16;
const NAVBAR_HEIGHT = 60;
const POPUP_HEIGHT = window.innerHeight - NAVBAR_HEIGHT - 2 * POPUP_TOP_BOTTOM_MARGIN;

const DEFAULT_DEBOUNCE_TIMEOUT = 50;
const MOBILE_VERSION_BREAKPOINT = 768;

export function EmailMakerBlockManager({gjsEditor}: EmailMakerBlockManagerProps): ReactElement<any> {
  const blockManager = gjsEditor.BlockManager;
  const dispatch = useAppDispatch();
  const selectedCategory = useSelectedBlockPanel();
  const showLoginDialog = !isUserLoggedIn();
  const showUpsellForAiEdit = !isUserPremium();

  const [aiEditEnabled, setAiEditEnabled] = useState<boolean>(false);
  const [hasUserSeenSections, setHasUserSeenSections] = useState<boolean>(true);
  const [hasUserSeenElements, setHasUserSeenElements] = useState<boolean>(true);
  const [isMobileScreen, setIsMobileScreen] = useState<boolean>(false);

  const BLOCK_CONSTANTS = window.PMW.emails.blocks;
  const CATEGORIES_WITH_PANEL = [BLOCK_CONSTANTS.CATEGORY_SECTIONS, BLOCK_CONSTANTS.CATEGORY_ELEMENTS, BLOCK_CONSTANTS.CATEGORY_TEXT, BLOCK_CONSTANTS.CATEGORY_IMAGE];
  const CATEGORIES_WITHOUT_PANEL = [BLOCK_CONSTANTS.CATEGORY_MY_DESIGNS];
  const SPECIAL_BLOCKS = [BLOCK_CONSTANTS.BLOCK_AI_EDIT];

  const blocksMap = blockManager.getAll().reduce((blockCategoryMap: BlockCategoryMap, item: Block) => {
    const category = item.getCategoryLabel();
    const categoryItem = blockCategoryMap[category];

    if (!categoryItem) {
      blockCategoryMap[category] = [item];
    } else {
      categoryItem.push(item);
    }

    return blockCategoryMap;
  }, {} as BlockCategoryMap);

  Object.keys(blocksMap).forEach((category) => {
    blocksMap[category].sort((block1, block2) => {
      const sortPriority1 = block1.get('attributes')?.sortPriority as number;
      const sortPriority2 = block2.get('attributes')?.sortPriority as number;
      return sortPriority1 - sortPriority2;
    });
  });

  const getIcon = (category: string): string => {
    switch (category) {
      case BLOCK_CONSTANTS.CATEGORY_SECTIONS:
        return 'icon-section';
      case BLOCK_CONSTANTS.CATEGORY_ELEMENTS:
        return 'icon-elements';
      case BLOCK_CONSTANTS.CATEGORY_TEXT:
        return 'icon-text';
      case BLOCK_CONSTANTS.CATEGORY_IMAGE:
        return 'icon-image';
      case BLOCK_CONSTANTS.CATEGORY_MY_DESIGNS:
        return 'icon-designs';
      case BLOCK_CONSTANTS.BLOCK_AI_EDIT:
        return 'icon-ai';
      default:
        return 'icon-exclamation-triangle';
    }
  };

  const getTextLabel = (category: string): string => {
    switch (category) {
      case BLOCK_CONSTANTS.CATEGORY_SECTIONS:
        return window.i18next.t('pmwjs_sections');
      case BLOCK_CONSTANTS.CATEGORY_ELEMENTS:
        return window.i18next.t('pmwjs_elements');
      case BLOCK_CONSTANTS.CATEGORY_TEXT:
        return window.i18next.t('pmwjs_text');
      case BLOCK_CONSTANTS.CATEGORY_IMAGE:
        return window.i18next.t('pmwjs_image');
      case BLOCK_CONSTANTS.CATEGORY_MY_DESIGNS:
        return window.i18next.t('pmwjs_my_designs');
      case BLOCK_CONSTANTS.BLOCK_AI_EDIT:
        return window.i18next.t('pmwjs_ai_edit');
      default:
        return 'Unknown Block';
    }
  };

  const getDescription = (category: string): string => {
    switch (category) {
      case BLOCK_CONSTANTS.CATEGORY_SECTIONS:
        return window.i18next.t('pmwjs_sections_block_description');
      case BLOCK_CONSTANTS.CATEGORY_ELEMENTS:
        return window.i18next.t('pmwjs_elements_block_description');
      case BLOCK_CONSTANTS.CATEGORY_TEXT:
        return window.i18next.t('pmwjs_text_block_description');
      case BLOCK_CONSTANTS.CATEGORY_IMAGE:
        return window.i18next.t('pmwjs_image_block_description');
      case BLOCK_CONSTANTS.CATEGORY_MY_DESIGNS:
        return window.i18next.t('pmwjs_my_designs_block_description');
      case BLOCK_CONSTANTS.BLOCK_AI_EDIT:
        return window.i18next.t('pmwjs_ai_edit_block_description');
      default:
        return 'Unknown Block';
    }
  };

  const getIntroPopupImage = (category: string): ReactElement<React.RefAttributes<HTMLImageElement>, typeof Image> | null => {
    let imageSource = '';
    switch (category) {
      case BLOCK_CONSTANTS.CATEGORY_SECTIONS:
        imageSource = 'section-block-intro.png';
        break;
      case BLOCK_CONSTANTS.CATEGORY_ELEMENTS:
        imageSource = 'elements-block-intro.png';
        break;
      case BLOCK_CONSTANTS.CATEGORY_TEXT:
        imageSource = 'text-block-intro.png';
        break;
      case BLOCK_CONSTANTS.CATEGORY_IMAGE:
        imageSource = 'image-block-intro.png';
        break;
      case BLOCK_CONSTANTS.CATEGORY_MY_DESIGNS:
        imageSource = 'my-designs-block-intro.png';
        break;
      default:
        return null;
    }

    return <Image src={getAssetUrl(imageSource)} alt={window.i18next.t('pmwjs_image')} aria-hidden />;
  };

  const getIntroPopupDescription = (category: string): ReactElement<React.RefAttributes<HTMLAnchorElement | HTMLSpanElement>, typeof Text> | null => {
    let description = '';
    switch (category) {
      case BLOCK_CONSTANTS.CATEGORY_SECTIONS:
        description = window.i18next.t('pmwjs_sections_block_intro');
        break;
      case BLOCK_CONSTANTS.CATEGORY_ELEMENTS:
        description = window.i18next.t('pmwjs_elements_block_intro');
        break;
      case BLOCK_CONSTANTS.CATEGORY_TEXT:
        description = window.i18next.t('pmwjs_text_block_intro');
        break;
      case BLOCK_CONSTANTS.CATEGORY_IMAGE:
        description = window.i18next.t('pmwjs_image_block_intro');
        break;
      case BLOCK_CONSTANTS.CATEGORY_MY_DESIGNS:
        description = window.i18next.t('pmwjs_my_designs_block_intro');
        break;
      default:
        return null;
    }

    return <Text val={description} size={TextSize.XSMALL} />;
  };

  const getActivityIndicator = (category: string): ReactElement<any> | null => {
    if ((category === BLOCK_CONSTANTS.CATEGORY_SECTIONS && !hasUserSeenSections) || (category === BLOCK_CONSTANTS.CATEGORY_ELEMENTS && !hasUserSeenElements)) {
      return <PulsatingDot containerClassname={styles.activityIndicator} />;
    }

    return null;
  };

  const getBlockIntroPopup = (category: string): ReactElement<any> => {
    const popupImage = getIntroPopupImage(category);
    const popupDescription = getIntroPopupDescription(category);

    if (!popupImage || !popupDescription) {
      return <></>;
    }

    const popup = (
      <div className={styles.introPopup}>
        {popupImage}
        {popupDescription}
      </div>
    );

    return (
      <div className={styles.introPopupContainer}>
        <Dropdown
          open
          popup={popup}
          alignment={ALIGNMENT_TYPE.RIGHT_START}
          selector={<div />}
          position={DROPDOWN_POSITION.ABSOLUTE}
          useDefaultArrow
          leftOffset={INTRO_POPUP_OFFSET_LEFT}
          arrowOffset={INTRO_POPUP_ARROW_OFFSET}
          popUpHasCustomWidth
          popUpHasCustomHeight
          arrowClasses={styles.popupPanelArrow}
          popupClassName={`${styles.popupPanel} ${styles.dynamicHeight}`}
        />
      </div>
    );
  };

  const getSelectorComponentLabelAndIcon = (category: string, extraClasses = '', textClasses = ''): ReactElement<any> => {
    const isSelected = selectedCategory === category;
    return (
      <div className={`${styles.block} ${extraClasses} ${isSelected ? styles.selected : ''}`}>
        {getActivityIndicator(category)}
        <Icon icon={getIcon(category)} size={IconSize.SIZE_ICON_24} className={textClasses} />
        <Text val={getTextLabel(category)} size={TextSize.XXSMALL} bold={isSelected} className={textClasses} />
      </div>
    );
  };

  const getSelectorComponent = (category: string, extraClasses = '', textClasses = ''): ReactElement<any> => {
    return (
      <div>
        <OverlayTrigger
          trigger={['hover', 'focus']}
          delay={{show: INTRO_POPUP_SHOW_DELAY_MS, hide: INTRO_POPUP_HIDE_DELAY_MS}}
          overlay={getBlockIntroPopup(category)}
          show={selectedCategory ? false : undefined}
        >
          {getSelectorComponentLabelAndIcon(category, extraClasses, textClasses)}
        </OverlayTrigger>
      </div>
    );
  };

  const getDefaultMobilePanel = (): ReactElement<any> => {
    return (
      <>
        <div className={styles.mobilePanelHeader}>
          <MobilePanelHeader headerLabel={window.i18next.t('pmwjs_add_to_email')} />
        </div>
        <div className={styles.mobileAllBlocks}>{getAllBlocks()}</div>
      </>
    );
  };

  const closePanelHandler = (): void => {
    dispatch(setSelectedBlockPanel(null));
  };

  const getPanelContent = (category: string | null): ReactElement<any> => {
    const props = {
      blocksMap,
      showBackButton: isMobileScreen,
      onBackButton: closePanelHandler,
    };

    switch (category) {
      case BLOCK_CONSTANTS.CATEGORY_SECTIONS:
        return <SectionsBlockPanel {...props} headerLabel={window.i18next.t('pmwjs_sections')} />;
      case BLOCK_CONSTANTS.CATEGORY_ELEMENTS:
        return <ElementsBlockPanel {...props} headerLabel={window.i18next.t('pmwjs_elements')} />;
      case BLOCK_CONSTANTS.CATEGORY_TEXT:
        return <TextBlockPanel {...props} headerLabel={isMobileScreen ? window.i18next.t('pmwjs_text') : undefined} />;
      case BLOCK_CONSTANTS.CATEGORY_IMAGE:
        return <ImageBlockPanel {...props} headerLabel={window.i18next.t('pmwjs_image')} />;
      default:
        return <div>{isMobileScreen ? getDefaultMobilePanel() : <> Panel Content not defined </>}</div>;
    }
  };

  const getBlockWithoutPanel = (category: string): ReactElement<any> => {
    return (
      <>
        {blocksMap[category].map((block) => {
          return (
            <EmailMakerBlock block={block} extraClasses={`${styles.block} spacing-p-0`} key={getUniqueString()}>
              {getSelectorComponent(block.getCategoryLabel())}
            </EmailMakerBlock>
          );
        })}
      </>
    );
  };

  const getBlockWithPanel = (category: string): ReactElement<any> => {
    const popupClasses = `${styles.popup} ${category === BLOCK_CONSTANTS.CATEGORY_SECTIONS ? styles.sectionsPopup : ''} ${category === BLOCK_CONSTANTS.CATEGORY_ELEMENTS ? styles.elementsPopup : ''}`;
    const popup = <div className={popupClasses}>{getPanelContent(category)}</div>;

    const openHandler = (): void => {
      dispatch(setSelectedBlockPanel(category));
      markSectionAsSeen(category);
      trackGA4EventOnPanelOpen(category);
    };

    return (
      <Dropdown
        open={selectedCategory === category}
        doCloseOnInnerClick={false}
        doesCloseOnOuterClick={false}
        alignment={ALIGNMENT_TYPE.RIGHT}
        position={DROPDOWN_POSITION.ABSOLUTE}
        selector={getSelectorComponent(category, styles.subSectionBlock)}
        popup={popup}
        maxHeight={POPUP_HEIGHT}
        leftOffset={PANEL_OFFSET_LEFT}
        arrowOffset={PANEL_ARROW_OFFSET}
        popUpHasCustomWidth
        useDefaultArrow
        arrowClasses={styles.popupPanelArrow}
        popupClassName={`${styles.popupPanel} ${category === BLOCK_CONSTANTS.CATEGORY_ELEMENTS ? styles.dynamicHeight : ''}`}
        popupId={POPUP_PANEL_ID}
        popupSpacingClass={styles.popupPanelSpacing}
        key={getUniqueString()}
        onOpen={openHandler}
        onClose={closePanelHandler}
      />
    );
  };

  const getSpecialBlock = (category: string): ReactElement<null, typeof ClickableDiv> | null => {
    switch (category) {
      case BLOCK_CONSTANTS.BLOCK_AI_EDIT:
        return aiEditBlock();
      default:
        return null;
    }
  };

  const aiEditClickHandler = (): void => {
    if (showLoginDialog) {
      openAuthenticateModal({
        fbPermissions: '',
        signupSource: AI_EDIT_SIGNUP_SOURCE,
        panelText: window.i18next.t('pmwjs_make_account_to_save_email'),
        panelSubtext: window.i18next.t('pmwjs_login_emails_promotion_subtext'),
        onLoginSuccess: async () => {
          await gjsEditor.runCommand(window.PMW.emails.commands.SAVE_TO_DB_AND_CACHE);
          window.location.reload();
        },
      });

      return;
    }

    if (showUpsellForAiEdit) {
      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_AI_REWRITE_EMAIL_CAMPAIGN_TEXT_COMPONENTS_CONTENT);

      return;
    }

    gjsEditor.runCommand(window.PMW.emails.commands.TOGGLE_AI_EDIT, {enable: !aiEditEnabled});
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_AI_EDIT_CLICKED);
    setAiEditEnabled(!aiEditEnabled);
  };

  const aiEditBlock = (): ReactElement<null, typeof ClickableDiv> => {
    const isAiEditBlockEnabled = window.PMW.emails.AI_EDIT_ENABLED;
    const extraClasses = `${styles.aiEditBlock} ${isAiEditBlockEnabled ? styles.selected : ''}`;

    return (
      <ClickableDiv key={getUniqueString()} onClick={aiEditClickHandler} className={styles.aiEditBlockContainer}>
        {isAiEditBlockEnabled ? <div className={styles.aiEditSelectedBackground} /> : null}
        {getSelectorComponent(BLOCK_CONSTANTS.BLOCK_AI_EDIT, extraClasses, styles.aiTextColor)}
        {showUpsellForAiEdit ? (
          <Icon icon="icon-crown" type={IconType.PREMIUM} shape={IconShape.NONE} size={IconSize.SIZE_ICON_20} className={`${styles.premiumTagIcon} ${styles.premiumTagDesktop}`} />
        ) : null}
      </ClickableDiv>
    );
  };

  const getMobileBlockVisualComponent = (category: string, containerClasses = '', iconContainerClasses = '', textClasses = ''): ReactElement<any> => {
    return (
      <div className={`${styles.mobileBlockVisualContainer} ${containerClasses}`}>
        <div className={`${styles.mobileIconContainer} ${iconContainerClasses}`}>
          <div className={styles.mobileIconShadowContainer}>
            <Icon icon={getIcon(category)} className={styles.mobileIcon} size={IconSize.SIZE_ICON_16} />
          </div>
        </div>

        <div className={styles.mobileBlockTextContainer}>
          <Text val={getTextLabel(category)} size={TextSize.XSMALL} bold className={textClasses} />
          <Text val={getDescription(category)} size={TextSize.XXSMALL} className={textClasses} />
        </div>
      </div>
    );
  };

  const getMobileBlockWithPanel = (category: string): ReactElement<any> => {
    return (
      <ClickableDiv
        key={getUniqueString()}
        onClick={() => {
          dispatch(setSelectedBlockPanel(category));
        }}
      >
        {getMobileBlockVisualComponent(category)}
      </ClickableDiv>
    );
  };

  const getMobileBlockWithoutPanel = (category: string): ReactElement<any> => {
    return (
      <>
        {blocksMap[category].map((block) => {
          return (
            <EmailMakerBlock block={block} key={getUniqueString()}>
              {getMobileBlockVisualComponent(category)}
            </EmailMakerBlock>
          );
        })}
      </>
    );
  };

  const getMobileSpecialBlock = (category: string): ReactElement<null, typeof ClickableDiv> | null => {
    switch (category) {
      case BLOCK_CONSTANTS.BLOCK_AI_EDIT:
        return getMobileAiEditBlock();
      default:
        return null;
    }
  };

  const getMobileAiEditBlock = (): ReactElement<null, typeof ClickableDiv> => {
    const bannerProps = {
      style: BannerStyle.AI,
      message: window.i18next.t('pmwjs_ai_edit_now_enabled'),
      icon: 'icon-ai',
      ctaText: window.i18next.t('pmwjs_turn_off_ai_edit'),
      cta: (): void => {
        gjsEditor.runCommand(window.PMW.emails.commands.HIDE_BANNER);
        gjsEditor.runCommand(window.PMW.emails.commands.TOGGLE_AI_EDIT, {enable: false});
        setAiEditEnabled(false);
      },
    };

    const buttonCta = (): void => {
      if (!window.PMW.emails.AI_EDIT_ENABLED) {
        gjsEditor.runCommand(window.PMW.emails.commands.SHOW_BANNER, bannerProps);
      } else {
        gjsEditor.runCommand(window.PMW.emails.commands.HIDE_BANNER);
      }

      gjsEditor.runCommand(window.PMW.emails.commands.CLOSE_ADD_BLOCKS_DIALOG);
      aiEditClickHandler();
    };

    const containerClasses = `${styles.aiEditBlock} ${aiEditEnabled ? styles.selected : ''}`;

    return (
      <ClickableDiv key={getUniqueString()} onClick={buttonCta}>
        {getMobileBlockVisualComponent(BLOCK_CONSTANTS.BLOCK_AI_EDIT, containerClasses, styles.aiEditIconContainer, styles.aiTextColor)}
        {showUpsellForAiEdit ? (
          <Icon icon="icon-crown" type={IconType.PREMIUM} shape={IconShape.NONE} size={IconSize.SIZE_ICON_20} className={`${styles.premiumTagIcon} ${styles.premiumTagMobile}`} />
        ) : null}
      </ClickableDiv>
    );
  };

  const getAllBlocks = (): ReactElement<any> => {
    const blockWithPanel = (category: string): ReactElement<any> => {
      if (isMobileScreen) {
        return getMobileBlockWithPanel(category);
      }

      return getBlockWithPanel(category);
    };

    const blockWithoutPanel = (category: string): ReactElement<any> => {
      if (isMobileScreen) {
        return getMobileBlockWithoutPanel(category);
      }

      return getBlockWithoutPanel(category);
    };

    const specialBlock = (category: string): ReactElement<any> | null => {
      if (isMobileScreen) {
        return getMobileSpecialBlock(category);
      }

      return getSpecialBlock(category);
    };

    return (
      <>
        {CATEGORIES_WITH_PANEL.map((category) => {
          return blockWithPanel(category);
        })}
        {CATEGORIES_WITHOUT_PANEL.map((category) => {
          return blockWithoutPanel(category);
        })}
        {SPECIAL_BLOCKS.map((category) => {
          return specialBlock(category);
        })}
      </>
    );
  };

  const markSectionAsSeen = (category: string): void => {
    if (category === BLOCK_CONSTANTS.CATEGORY_SECTIONS && !hasUserSeenSections) {
      setHasUserSeenSections(true);
      void markUserHasSeenSectionsBlock();
    } else if (category === BLOCK_CONSTANTS.CATEGORY_ELEMENTS && !hasUserSeenElements) {
      setHasUserSeenElements(true);
      void markUserHasSeenElementsBlock();
    }
  };

  const fetchAllDataIntoRedux = async (): Promise<void> => {
    await Promise.all([getAllEmailComponentTemplateCategories(), getAllEmailComponentTemplates(), getUserEmailComponentTemplates()]).then(
      ([categories, templates, userTemplates]) => {
        dispatch(setCategories(categories));
        dispatch(setTemplates(templates));
        dispatch(setUserTemplates(userTemplates));
      }
    );
  };

  const isMobileScreenWidth = (screenWidth: number): boolean => {
    return screenWidth <= MOBILE_VERSION_BREAKPOINT;
  };

  const resizeHandler = window.PMW.debounce((): void => {
    const isCurrentWindowWidthMobile = isMobileScreenWidth(window.innerWidth);
    setIsMobileScreen(isCurrentWindowWidthMobile);
  }, DEFAULT_DEBOUNCE_TIMEOUT);

  const closePopupListener = (e: MouseEvent | TouchEvent): void => {
    const target = e.target as HTMLElement;

    if (!document.getElementById(window.PMW.emails.blocks.ID_BLOCKS_MANAGER_CONTAINER)?.contains(target)) {
      closePanelHandler();
    }
  };

  const trackGA4EventOnPanelOpen = (category: string): void => {
    if (category === BLOCK_CONSTANTS.CATEGORY_SECTIONS) {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_ADD_SECTION);
    }
  };

  const aiEditClickOutsideListener = (event: MouseEvent | TouchEvent): void => {
    if (isMobileScreen) {
      return;
    }

    const target = event.target as HTMLElement;
    const editorContainer = document.getElementById('html-editor-canvas');

    const isAiImagesModalOpen = isModalOpen();
    const clickedOnCanvas = editorContainer?.contains(target);

    if (!clickedOnCanvas && !isAiImagesModalOpen && aiEditEnabled) {
      gjsEditor.runCommand(window.PMW.emails.commands.TOGGLE_AI_EDIT, {enable: false});
      setAiEditEnabled(false);
    }
  };

  const aiEditKeydownHandler = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && aiEditEnabled) {
      gjsEditor.runCommand(window.PMW.emails.commands.TOGGLE_AI_EDIT, {enable: false});
      setAiEditEnabled(false);
    }
  };

  useEffect(() => {
    void hasUserSeenSectionsBlock().then((hasSeen) => {
      setHasUserSeenSections(hasSeen);
    });
    void hasUserSeenElementsBlock().then((hasSeen) => {
      setHasUserSeenElements(hasSeen);
    });
    void fetchAllDataIntoRedux();

    resizeHandler();
    window.addEventListener('resize', resizeHandler, {passive: true});
    document.addEventListener('touchstart', closePopupListener);
    document.addEventListener('mousedown', closePopupListener);

    return (): void => {
      resizeHandler.clear();
      window.removeEventListener('resize', resizeHandler);
      document.removeEventListener('touchstart', closePopupListener);
      document.removeEventListener('mousedown', closePopupListener);
    };
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', aiEditKeydownHandler);
    window.addEventListener('mousedown', aiEditClickOutsideListener);

    return (): void => {
      document.removeEventListener('keydown', aiEditKeydownHandler);
      window.removeEventListener('mousedown', aiEditClickOutsideListener);
    };
  }, [aiEditEnabled]);

  const getBlockManager = (): ReactElement<any> => {
    if (isMobileScreen) {
      return <IsMobileScreenContext.Provider value={isMobileScreen}>{getPanelContent(selectedCategory)}</IsMobileScreenContext.Provider>;
    }

    return (
      <div className={styles.blocksManager} key={getUniqueString()}>
        {getAllBlocks()}
      </div>
    );
  };

  return <GrapesjsEditorContext.Provider value={gjsEditor}>{getBlockManager()}</GrapesjsEditorContext.Provider>;
}
